/**
 * XRMultiviewDetector — detects and configures multiview rendering.
 *
 * Checks for OVR_multiview2 extension availability and configures
 * the Three.js renderer for multiview stereo rendering when available.
 */

import type * as THREE from 'three/webgpu';
import { Logger } from '../core/Logger.js';

export class XRMultiviewDetector {
  private _available = false;
  private _enabled = false;

  /**
   * Detect multiview support from the renderer's GL context.
   */
  detect(renderer: THREE.WebGPURenderer): boolean {
    try {
      // Multiview is only available on WebGL 2
      const gl = (renderer as any).getContext?.();
      if (!gl) {
        Logger.debug('XRMultiview', 'No GL context available');
        return false;
      }

      // Check for OVR_multiview2 extension
      const ext = gl.getExtension?.('OVR_multiview2') ??
                  gl.getExtension?.('OCULUS_multiview');

      if (ext) {
        this._available = true;
        Logger.info('XRMultiview', 'OVR_multiview2 extension available');
      } else {
        Logger.debug('XRMultiview', 'OVR_multiview2 extension not available');
      }

      return this._available;
    } catch {
      Logger.debug('XRMultiview', 'Failed to detect multiview support');
      return false;
    }
  }

  /**
   * Enable multiview on the renderer's XR manager.
   */
  enable(_renderer: THREE.WebGPURenderer): boolean {
    if (!this._available) return false;

    // DELIBERATELY DISABLED. In r185 multiview is a constructor-time opt-in
    // (`new WebGPURenderer({ multiview: true })`) — `renderer.xr.useMultiview`
    // is a read-only METHOD, and the old `(xr as any).useMultiview = true`
    // write CLOBBERED it with a boolean while enabling nothing. Even post-hoc
    // enabling would be wrong today: multiview breaks the right eye's
    // projection on the WebGL backend (three.js issue #32538, open as of
    // r185.1). Re-evaluate when upstream fixes it.
    Logger.info('XRMultiview', 'OVR_multiview2 available but NOT enabled (three #32538 right-eye projection bug)');
    return false;
  }

  get available(): boolean {
    return this._available;
  }

  get enabled(): boolean {
    return this._enabled;
  }
}
